/**
 * PURPOSE: Ver en grande la foto de una opción de uniforme haciendo clic sobre la propia foto (FTR-0039).
 * RESPONSIBILITIES: `EnlargeablePhoto` convierte la miniatura existente en el disparador — sin ícono, botón ni
 *   texto adicional: el único cambio visible es el cursor — y `UniformPhotoLightbox` muestra la foto completa
 *   (object-contain, sin recorte) en el patrón de modal de Winscore: Joy Modal con su cierre de siempre (X,
 *   clic fuera y Escape). Es un Joy Modal para apilarse bien sobre otros Joy Modal (Programación) y sobre los
 *   paneles propios de Administración (z-[70]); se monta solo mientras está abierto. El clic se detiene en la
 *   foto para no disparar la acción del contenedor (seleccionar una opción de react-select, abrir su menú).
 * COLLABORATORS: UniformOptionVisual.jsx, UniformOptionEditor.jsx, MatchUniformOptionsFields.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/UniformPhotoLightbox.jsx
 */
import React, { useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import ModalDialog from "@mui/joy/ModalDialog";

const UniformPhotoLightbox = ({ open, photoUrl, label = "", onClose }) => {
  if (!open || !photoUrl) return null;
  const alt = label ? `Foto ampliada: ${label}` : "Foto ampliada del uniforme";
  return (
    <Modal open onClose={onClose}>
      <ModalDialog
        aria-label={alt}
        layout="center"
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: "94vw", maxHeight: "94vh", overflow: "auto", gap: 1 }}
      >
        <ModalClose variant="plain" aria-label="Cerrar foto" />
        <img
          src={photoUrl}
          alt={alt}
          className="mx-auto block max-h-[80vh] max-w-full rounded-lg object-contain"
        />
        {label && <p className="px-8 text-center text-sm font-semibold text-slate-700">{label}</p>}
      </ModalDialog>
    </Modal>
  );
};

/**
 * La miniatura es el control. `onEnlarge` delega la apertura en el contenedor (necesario dentro de react-select,
 * cuyo menú se desmonta al perder el foco); sin él, la foto maneja su propio visor.
 */
export const EnlargeablePhoto = ({ photoUrl, label = "", className = "", tabIndex, onEnlarge, children }) => {
  const [open, setOpen] = useState(false);

  const stop = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClick = (event) => {
    stop(event);
    if (onEnlarge) onEnlarge({ photoUrl, label });
    else setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onMouseDown={stop}
        onClick={handleClick}
        tabIndex={tabIndex}
        aria-label={label ? `Ampliar foto: ${label}` : "Ampliar foto del uniforme"}
        className={`inline-flex shrink-0 cursor-zoom-in items-center justify-center rounded border-0 bg-transparent p-0 leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${className}`}
      >
        {children}
      </button>
      {!onEnlarge && (
        <UniformPhotoLightbox open={open} photoUrl={photoUrl} label={label} onClose={() => setOpen(false)} />
      )}
    </>
  );
};

export default UniformPhotoLightbox;
